import {
  LiveCreatorEventBatch,
  LiveCreatorUserEvent,
} from '../interfaces/live-creator.contract';

interface InFlightBatch {
  batch: LiveCreatorEventBatch;
}

function eventKey(event: LiveCreatorUserEvent): string {
  if (event.dbId) return `${event.type}:node:${event.dbId}`;
  if (event.relation) return `${event.type}:relation:${event.relation}`;
  return `${event.type}:global`;
}

/**
 * Request-scoped semantic event journal.
 *
 * This intentionally does NOT capture pointer/mousemove events.
 * The Creator should call record() only after a meaningful semantic change
 * has committed to its local Canvas state.
 */
export class LiveCreatorEventJournal {
  private pending: LiveCreatorUserEvent[] = [];
  private inFlight: InFlightBatch | null = null;

  record(event: LiveCreatorUserEvent): void {
    this.pending.push(structuredClone(event));
    this.pending = this.compact(this.pending);
  }

  beginBatch(
    fromRevision: number,
    toRevision: number,
    createId: () => string,
  ): LiveCreatorEventBatch | null {
    if (this.inFlight) return structuredClone(this.inFlight.batch);
    if (this.pending.length === 0) return null;

    const batch: LiveCreatorEventBatch = {
      batch_id: createId(),
      from_revision: fromRevision,
      to_revision: toRevision,
      events: this.pending,
    };

    // Pending is not permanently discarded. The in-flight copy is retained
    // until ACK. New user actions start accumulating into a fresh pending list.
    this.inFlight = { batch };
    this.pending = [];
    return structuredClone(batch);
  }

  ack(batchId: string): boolean {
    if (!this.inFlight || this.inFlight.batch.batch_id !== batchId) {
      return false;
    }
    this.inFlight = null;
    return true;
  }

  nack(batchId: string): boolean {
    if (!this.inFlight || this.inFlight.batch.batch_id !== batchId) {
      return false;
    }

    // Reinsert old events before newer pending events, then compact.
    this.pending = this.compact([
      ...this.inFlight.batch.events,
      ...this.pending,
    ]);
    this.inFlight = null;
    return true;
  }

  currentInFlight(): LiveCreatorEventBatch | null {
    return this.inFlight ? structuredClone(this.inFlight.batch) : null;
  }

  currentPending(): readonly LiveCreatorUserEvent[] {
    return structuredClone(this.pending);
  }

  private compact(events: LiveCreatorUserEvent[]): LiveCreatorUserEvent[] {
    let result: LiveCreatorUserEvent[] = [];
    for (const event of events) {
      const sameTarget = (candidate: LiveCreatorUserEvent): boolean =>
        event.dbId !== undefined ? candidate.dbId === event.dbId :
          event.relation !== undefined ? candidate.relation === event.relation : false;
      const removal = event.type === 'NODE_REMOVED' || event.type === 'CONNECTION_REMOVED';
      if (removal) {
        const added = result.some(candidate => sameTarget(candidate) &&
          (candidate.type === 'NODE_ADDED' || candidate.type === 'CONNECTION_ADDED'));
        result = result.filter(candidate => !sameTarget(candidate));
        if (!added) result.push(event);
        continue;
      }
      const lww = event.type === 'NODE_MOVED' || event.type === 'NODE_RESIZED' ||
        event.type === 'NODE_STYLE_CHANGED' || event.type === 'CONNECTION_STYLE_CHANGED' ||
        event.type === 'VIEW_SAVED' || event.type === 'VIEW_RELOADED';
      const index = lww ? result.findIndex(candidate => eventKey(candidate) === eventKey(event)) : -1;
      if (index >= 0) {
        // Style/geometry events can contain partial fields; preserve untouched fields.
        const previous = result[index];
        result[index] = { ...event, payload: { ...previous.payload, ...event.payload } };
      } else result.push(event);
    }
    return result;
  }
}
